import React from 'react';
import { Link } from 'react-router-dom';
import styles from './ActionButton.module.css';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ActionButtonProps {
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'info' | 'close';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  loading?: boolean;
  isLoading?: boolean; // Alternative prop name for loading
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  className?: string;
  fullWidth?: boolean;
  tooltip?: string;
  ariaLabel?: string;
  target?: string;
  [key: string]: any; // For additional props
}

const ActionButton: React.FC<ActionButtonProps> = ({
  children,
  onClick,
  href,
  type = 'button',
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  isLoading = false,
  icon,
  iconPosition = 'left',
  className = '',
  fullWidth = false,
  tooltip,
  ariaLabel,
  target,
  ...props
}) => {
  // Filter out non-DOM props
  const {
    isLoading: _isLoading,
    ...domProps
  } = props;

  // Use either loading or isLoading prop
  const isActuallyLoading = loading || isLoading;

  // Determine the base class names
  const baseClasses = [
    styles.actionButton,
    styles[`variant-${variant}`],
    styles[`size-${size}`],
    fullWidth && styles.fullWidth,
    isActuallyLoading && styles.loading,
    disabled && styles.disabled,
    className
  ].filter(Boolean).join(' ');

  const displayIcon = variant === 'close' ? <XMarkIcon className={styles.icon} /> : icon;

  // Common button content
  const buttonContent = (
    <>
      {isActuallyLoading && <span className={styles.spinner} />}
      {!isActuallyLoading && displayIcon && iconPosition === 'left' && (
        <span className={styles.iconLeft}>{displayIcon}</span>
      )}
      {children && <span className={styles.buttonText}>{children}</span>}
      {!isActuallyLoading && displayIcon && iconPosition === 'right' && (
        <span className={styles.iconRight}>{displayIcon}</span>
      )}
    </>
  );

  // If href is provided, render as Link or anchor
  if (href && !disabled) {
    // Internal link
    if (href.startsWith('/')) {
      return (
        <Link 
          to={href} 
          className={baseClasses}
          title={tooltip}
          aria-label={ariaLabel}
          target={target}
          {...domProps}
        >
          {buttonContent}
        </Link>
      );
    }
    // External link
    return (
      <a
        href={href}
        className={baseClasses}
        title={tooltip}
        aria-label={ariaLabel}
        target={target || '_blank'}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        {...domProps}
      >
        {buttonContent}
      </a>
    );
  }

  // Regular button
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    console.log('ActionButton: Button clicked, onClick:', onClick);
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      type={type}
      className={baseClasses}
      onClick={handleClick}
      disabled={disabled || isActuallyLoading}
      title={tooltip}
      aria-label={ariaLabel}
      {...domProps}
    >
      {buttonContent}
    </button>
  );
};

export default ActionButton;
